import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import {
    Button,
    Dialog,
    Error,
    LoadingIndicator,
    Table,
    Box
} from 'components';
import { CheckFill, CrossFill } from 'components/icons';
import { Trans, useTranslation } from 'react-i18next';
import { useTable, useTransformations, useTransformationsCount } from 'hooks';
import { Transformation } from 'types/transformation';
import { useBlockNavigation } from 'providers';
import { Link } from 'react-router-dom';

interface COntentProps {
    close: () => void;
    setTransformationsToAssign: Dispatch<SetStateAction<Transformation[]>>;
    assignedTransformations: Transformation[];
    transformations: Transformation[];
}

const Content: FC<COntentProps> = ({
    close,
    transformations,
    setTransformationsToAssign,
    assignedTransformations
}) => {
    const { t } = useTranslation();
    const { setIsNavigationBlocked } = useBlockNavigation();

    const table = useTable<Transformation>({
        columns: useMemo(
            () => [
                {
                    Header: t('general.transformation_pattern'),
                    accessor: 'pattern'
                },
                {
                    Header: t('general.transformation_replacement'),
                    accessor: 'replacement'
                },
                {
                    Header: t('general.transformation_case_insensitive'),
                    accessor: 'caseInsensitive',
                    Cell: (row) =>
                        row.value ? (
                            <CheckFill color="primary" />
                        ) : (
                            <CrossFill color={['grey', 'A200']} />
                        ),
                    disableSortBy: true
                }
            ],
            [t]
        ),
        data: transformations,
        hasSelect: true,
        initialState: {
            selectedRowIds: transformations.reduce<Record<string, boolean>>(
                (acc, transformation, index) => {
                    if (
                        assignedTransformations.find(
                            (item) => item.id === transformation.id
                        )
                    ) {
                        acc[index] = true;
                    }

                    return acc;
                },
                {}
            )
        }
    });

    const handleSubmit = () => {
        setTransformationsToAssign(
            table.selectedFlatRows.map((row) => row.original)
        );
        setIsNavigationBlocked(true);
        close();
    };

    return (
        <>
            <Dialog.Header
                title={t('expressions.assign_transformation')}
                onClose={close}
            />
            <Dialog.Body>
                <LoadingIndicator />
                {transformations.length ? (
                    <Table<Transformation>
                        controller={table}
                        disablePagination
                    />
                ) : (
                    <Box my={4}>
                        <Trans
                            i18nKey="expressions.no_transformations"
                            components={[<Link to="/transformations" />]}
                        />
                    </Box>
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <Button
                    type="contained"
                    onClick={handleSubmit}
                    isDisabled={!table.selectedFlatRows.length}>
                    {t('general.Add')}
                </Button>
                <Button type="text" onClick={close}>
                    {t('general.Close')}
                </Button>
            </Dialog.Footer>
        </>
    );
};

interface AssignTransformationsDialogProps {
    isOpen: boolean;
    close: () => void;
    setTransformationsToAssign: Dispatch<SetStateAction<Transformation[]>>;
    assignedTransformations: Transformation[];
}

const AssignTransformationsDialog: FC<AssignTransformationsDialogProps> = ({
    isOpen,
    close,
    setTransformationsToAssign,
    assignedTransformations
}) => {
    const {
        data: count,
        isError: isCountError,
        error: countError,
        isLoading: isCountLoading
    } = useTransformationsCount();
    const {
        data: transformations,
        isError: isDataError,
        error: dataError,
        isLoading: isTransformationsLoading
    } = useTransformations({
        currentPage: 1,
        enabled: !isCountLoading,
        pageSize: count
    });

    return (
        <Dialog width={600} height={500} isOpen={isOpen} onClose={close}>
            {isDataError && <Error error={dataError} />}
            {isCountError && <Error error={countError} />}
            {!isTransformationsLoading && (
                <Content
                    transformations={transformations}
                    close={close}
                    setTransformationsToAssign={setTransformationsToAssign}
                    assignedTransformations={assignedTransformations}
                />
            )}
        </Dialog>
    );
};

export default AssignTransformationsDialog;
